import { format } from "date-fns";
import { Link } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Banner } from "@/components/ui/banner";
import { Button } from "@/components/ui/button";
import { useAmpsTasks } from "@/hooks/queries/useAmpsTasks";
import { useCalibrationStatus } from "@/hooks/queries/useCalibrationStatus";
import { useCalibrationSubmissions } from "@/hooks/queries/useCalibrationSubmissions";
import { CalibrationSubNav } from "@/routes/calibration/CalibrationSubNav";
import { CalibrationReviewNote } from "@/routes/calibration/review-flag-copy";
import { taskLabel } from "@/routes/calibration/task-label";
import type { AmpsTask } from "@/hooks/queries/amps-task-types";
import type { CalibrationSubmissionRow } from "@/hooks/queries/calibration-log-types";

// Story 8.8 (gh-247) — calibration completion screen (UX Screen 19, FR43/FR44).
// Route /calibration/complete SELF-GUARDS on `state === "complete"` + severity
// presence (checkpoint D5) instead of a router gate: `state` flips to complete only
// when the 8.5 profile row exists — 8.4/8.5 are backlog, so 5/5 scored with
// `state === "in_progress"` (severity absent) is a REAL window, and bypass
// (`already_certified`) users read complete WITHOUT severity. Both render the calm
// processing state below — NEVER a false success banner (AC5).
//
// Severity values arrive as EXACT decimal strings (Rule 2 — Pydantic serialises
// Decimal to string); rendered verbatim with `tabular-nums`, never parsed/rounded.
// Framed as neutral numeric outputs per UX Decision 3 — no pass/fail language.
//
// Deliberate deviations from the mockup (AC7 — do NOT "fix" back): single task
// label per case (gold-standard Decision #3; "F-1 / M-1" is stale-AC fiction);
// `tabular-nums` on value cards + dates (design-system non-negotiable the mockup
// CSS lacks); AppShell + CalibrationSubNav host (the approved 8.7 reconciliation).

function ValueCard({ label, value }: { label: string; value: string }) {
  // Flex column + `mt-auto` pins the value to the card bottom, so the two
  // cards' values sit on the same line even when one label wraps.
  return (
    <div className="flex flex-col gap-1 rounded-2xl border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] px-5 py-4">
      <h3 className="text-sm font-semibold text-[color:var(--text-tertiary)]">{label}</h3>
      {/* The exact decimal string, verbatim — never parseFloat/round (Rule 2). */}
      <p className="tabular-nums mt-auto text-2xl font-semibold text-[color:var(--text-primary)]">
        {value}
      </p>
    </div>
  );
}

function CompletedCasesTable({
  submissions,
  tasks,
}: {
  submissions: CalibrationSubmissionRow[];
  tasks: AmpsTask[] | undefined;
}) {
  return (
    <table className="w-full border-collapse text-sm">
      <thead>
        <tr className="border-b border-[color:var(--border-primary)] text-left text-[color:var(--text-tertiary)]">
          <th className="py-2 pr-3 font-semibold">Case</th>
          <th className="py-2 pr-3 font-semibold">Task</th>
          <th className="py-2 pr-3 font-semibold">Status</th>
          <th className="py-2 font-semibold">Processed</th>
        </tr>
      </thead>
      <tbody>
        {submissions.map((row) => (
          <tr
            key={row.case_id}
            className="border-b border-[color:var(--border-secondary)] last:border-b-0"
          >
            <td className="py-2 pr-3 font-semibold text-[color:var(--text-primary)]">
              Case {row.order}
            </td>
            {/* ONE task per case (gold-standard Decision #3) — see module note. */}
            <td className="py-2 pr-3 text-[color:var(--text-secondary)]">
              {taskLabel(tasks, row.task_code)}
            </td>
            <td className="py-2 pr-3">
              <Badge variant="ok">Scored</Badge>
            </td>
            <td className="tabular-nums py-2 text-[color:var(--text-secondary)]">
              {format(new Date(row.scored_at), "MMM dd, yyyy")}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function CalibrationCompleteScreen() {
  const statusQuery = useCalibrationStatus({ enabled: true });
  const tasksQuery = useAmpsTasks();

  const status = statusQuery.data;
  // The full-success render needs BOTH: the complete state AND numeric severity
  // (bypass users are complete with severity absent — they never calibrated, so
  // there are no values to show; the calm state below covers them too).
  const isComplete =
    status?.state === "complete" &&
    status.motor_severity != null &&
    status.process_severity != null;
  // The completed-cases table only exists in the full-success state — don't
  // fetch (and discard) the payload for the not-complete/error renders.
  const submissionsQuery = useCalibrationSubmissions({ enabled: isComplete });

  return (
    <section className="flex flex-col gap-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        {/* The title asserts completion ONLY in the proven-complete state — a
            mid-calibration deep-linker must not read "complete" above the
            not-complete Banner (AC5 posture extended to the page title). */}
        <h1 className="display-xs text-[color:var(--text-primary)]">
          {isComplete ? "Calibration complete" : "Calibration"}
        </h1>
        {isComplete ? <Badge variant="ok">Live scoring enabled</Badge> : null}
      </header>

      <CalibrationSubNav />

      {/* AC9 posture: quiet while pending; a failed status read gets a neutral
          couldn't-load state (never the misleading "not complete yet" domain
          claim — the case-log screen's non-404 posture); the not-complete window
          (5/5 scored but 8.4/8.5 processing, bypass users, or a deep link
          mid-calibration) gets a calm info state pointing back to the board —
          never a false success. */}
      {statusQuery.isPending ? null : statusQuery.isError ? (
        <>
          <Banner variant="info" title="Calibration status couldn’t load">
            <p>
              Your calibration progress couldn’t be loaded right now. Your
              recorded scores are safe — try again in a moment.
            </p>
          </Banner>
          <div>
            <Button asChild variant="secondary">
              <Link to="/calibration/cases">Go to case studies</Link>
            </Button>
          </div>
        </>
      ) : !isComplete ? (
        <>
          <Banner variant="info" title="Calibration not complete yet">
            <p>
              Your calibration values appear here once every case study is scored
              and processing completes. Continue with your case studies in the
              meantime.
            </p>
          </Banner>
          <div>
            <Button asChild variant="secondary">
              <Link to="/calibration/cases">Go to case studies</Link>
            </Button>
          </div>
        </>
      ) : (
        <>
          <Banner variant="success" title="Live scoring is now enabled.">
            <p>
              Your calibration values have been attached to this account and will
              be applied automatically in future scoring submissions.
            </p>
          </Banner>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="flex flex-col gap-3">
              <div>
                <h2 className="text-lg font-semibold text-[color:var(--text-primary)]">
                  Your calibration values
                </h2>
                <p className="text-sm text-[color:var(--text-tertiary)]">
                  Neutral reference values calculated from the completed
                  calibration case studies.
                </p>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <ValueCard
                  label="Motor calibration value"
                  value={status.motor_severity as string}
                />
                <ValueCard
                  label="Process calibration value"
                  value={status.process_severity as string}
                />
              </div>
              <CalibrationReviewNote status={status.calibration_review_status} />
            </div>

            <div className="flex flex-col gap-3">
              <div>
                <h2 className="text-lg font-semibold text-[color:var(--text-primary)]">
                  Completed case studies
                </h2>
                <p className="text-sm text-[color:var(--text-tertiary)]">
                  All required calibration cases now show as complete.
                </p>
              </div>
              {/* AC9: a failed list read gets its own calm Banner — never a
                  silent empty section under the completeness copy. Quiet while
                  pending, per posture. */}
              {submissionsQuery.isError ? (
                <Banner variant="info" title="Completed cases unavailable">
                  <p>
                    The completed case list couldn’t be loaded right now. Your
                    calibration values above are unaffected.
                  </p>
                </Banner>
              ) : submissionsQuery.data ? (
                <CompletedCasesTable
                  submissions={submissionsQuery.data.submissions}
                  tasks={tasksQuery.data}
                />
              ) : null}
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <Button asChild variant="secondary">
              <Link to="/workspace">Go to workspace</Link>
            </Button>
            {/* The one primary CTA on this view — forward into live scoring. */}
            <Button asChild variant="primary">
              <Link to="/clients/new">Start live evaluation</Link>
            </Button>
          </div>
        </>
      )}
    </section>
  );
}

export default CalibrationCompleteScreen;
